/**
 * 安全的日志管理系统
 * 在生产环境中自动禁用调试日志，防止敏感信息泄露
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

interface LogConfig {
  level: LogLevel;
  enableConsole: boolean;
  enableRemote: boolean;
  remoteEndpoint?: string;
}

class SecureLogger {
  private config: LogConfig;

  constructor() {
    this.config = {
      level: this.getLogLevelFromEnv(),
      enableConsole: import.meta.env.DEV,
      enableRemote: false,
    };
  }

  private getLogLevelFromEnv(): LogLevel {
    const level = import.meta.env.VITE_LOG_LEVEL?.toLowerCase();
    switch (level) {
      case 'debug': return LogLevel.DEBUG;
      case 'info': return LogLevel.INFO;
      case 'warn': return LogLevel.WARN;
      case 'error': return LogLevel.ERROR;
      default: return import.meta.env.DEV ? LogLevel.DEBUG : LogLevel.ERROR;
    }
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.config.level;
  }

  private sanitizeData(data: any): any {
    // 移除敏感信息
    if (typeof data === 'object' && data !== null) {
      const sanitized = { ...data };
      const sensitiveKeys = ['password', 'privateKey', 'secret', 'token', 'key'];
      
      for (const key of sensitiveKeys) {
        if (key in sanitized) {
          sanitized[key] = '[REDACTED]';
        }
      }
      
      return sanitized;
    }
    
    return data;
  }

  private formatMessage(level: string, message: string, ...args: any[]): string {
    const timestamp = new Date().toISOString();
    const sanitizedArgs = args.map(arg => this.sanitizeData(arg));
    return `[${timestamp}] [${level}] ${message} ${sanitizedArgs.length > 0 ? JSON.stringify(sanitizedArgs) : ''}`;
  }

  debug(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.DEBUG) && this.config.enableConsole) {
      console.log(this.formatMessage('DEBUG', message, ...args));
    }
  }

  info(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.INFO) && this.config.enableConsole) {
      console.info(this.formatMessage('INFO', message, ...args));
    }
  }

  warn(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.WARN) && this.config.enableConsole) {
      console.warn(this.formatMessage('WARN', message, ...args));
    }
  }

  error(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.ERROR) && this.config.enableConsole) {
      console.error(this.formatMessage('ERROR', message, ...args));
    }
  }

  // 安全的方法，用于记录用户操作（不包含敏感信息）
  userAction(action: string, userId?: string, metadata?: Record<string, any>): void {
    const sanitizedMetadata = this.sanitizeData(metadata);
    this.info(`User Action: ${action}`, {
      userId: userId ? `user_${userId.slice(0, 8)}` : 'anonymous',
      ...sanitizedMetadata
    });
  }

  // 安全的方法，用于记录系统事件
  systemEvent(event: string, metadata?: Record<string, any>): void {
    const sanitizedMetadata = this.sanitizeData(metadata);
    this.info(`System Event: ${event}`, sanitizedMetadata);
  }

  // 安全的方法，用于记录错误（自动脱敏）
  securityEvent(event: string, severity: 'low' | 'medium' | 'high', metadata?: Record<string, any>): void {
    const sanitizedMetadata = this.sanitizeData(metadata);
    const level = severity === 'high' ? LogLevel.ERROR : LogLevel.WARN;
    
    if (this.shouldLog(level)) {
      const message = `Security Event [${severity.toUpperCase()}]: ${event}`;
      if (level === LogLevel.ERROR) {
        console.error(this.formatMessage('SECURITY', message, sanitizedMetadata));
      } else {
        console.warn(this.formatMessage('SECURITY', message, sanitizedMetadata));
      }
    }
  }
}

// 导出单例实例
export const logger = new SecureLogger();

// 导出便捷方法
export const log = {
  debug: logger.debug.bind(logger),
  info: logger.info.bind(logger),
  warn: logger.warn.bind(logger),
  error: logger.error.bind(logger),
  userAction: logger.userAction.bind(logger),
  systemEvent: logger.systemEvent.bind(logger),
  securityEvent: logger.securityEvent.bind(logger),
};
